import type { InvoiceRepository } from "../ports/index.js";
import type { GetInvoiceQuery, GetInvoiceResult } from "./queries.js";
import type { Invoice } from "@invoicing-sdk/domain";

/**
 * Dependencies required for GetInvoice use case
 */
export interface GetInvoiceDependencies {
  readonly invoiceRepository: InvoiceRepository;
}

/**
 * Use case for retrieving a single invoice by ID, invoice number, or order ID
 * 
 * Requirements:
 * - 2.3: Allow retrieval by either orderId or invoiceNumber
 * - 2.4: Return either domain data or PDF bytes based on request type
 */
export function makeGetInvoice(deps: GetInvoiceDependencies) {
  return async (query: GetInvoiceQuery): Promise<GetInvoiceResult | null> => {
    const { invoiceRepository } = deps;
    const { invoiceId, invoiceNumber, orderId, includePdf = false } = query;

    // Validate that at least one identifier is provided
    if (!invoiceId && !invoiceNumber && !orderId) {
      throw new Error("At least one of invoiceId, invoiceNumber, or orderId must be provided");
    }

    // Find invoice using the provided identifier
    let invoice: Invoice | null = null;

    if (invoiceId) {
      invoice = await invoiceRepository.findById(invoiceId);
    } else if (invoiceNumber) {
      invoice = await invoiceRepository.findByInvoiceNumber(invoiceNumber);
    } else if (orderId) {
      invoice = await invoiceRepository.findByOrderId(orderId);
    }

    // Return null if invoice not found
    if (!invoice) {
      return null;
    }

    // Transform domain entity to result format
    const result: GetInvoiceResult = {
      invoiceId: invoice.invoiceId,
      invoiceNumber: invoice.invoiceNumber,
      orderId: invoice.orderId,
      customerSnapshot: {
        firstName: invoice.customerSnapshot.firstName,
        lastName: invoice.customerSnapshot.lastName,
        street: invoice.customerSnapshot.street,
        zip: invoice.customerSnapshot.zip,
        city: invoice.customerSnapshot.city,
        country: invoice.customerSnapshot.country,
        email: invoice.customerSnapshot.email,
        phone: invoice.customerSnapshot.phone,
      },
      itemsSnapshot: invoice.itemsSnapshot.map(item => ({
        name: item.name,
        variant: item.variant,
        productNumber: item.productNumber,
        unitPrice: item.unitPrice.toPlainObject(),
        quantity: item.quantity,
        linePrice: item.linePrice.toPlainObject(),
      })),
      financials: {
        subtotal: invoice.financials.subtotal.toPlainObject(),
        taxes: invoice.financials.taxes.toPlainObject(),
        shippingCost: invoice.financials.shippingCost.toPlainObject(),
        discount: invoice.financials.discount.toPlainObject(),
        grandTotal: invoice.financials.grandTotal.toPlainObject(),
      },
      createdOn: invoice.createdOn,
      createdBy: invoice.createdBy,
      // Include PDF bytes only if requested
      pdfBytes: includePdf ? invoice.pdfBytes : undefined,
    };

    return result;
  };
}

/**
 * Factory function to create GetInvoice use case with dependencies
 */
export function createGetInvoiceUseCase(
  deps: GetInvoiceDependencies
): (query: GetInvoiceQuery) => Promise<GetInvoiceResult | null> {
  return makeGetInvoice(deps);
}